import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { db, storage } from '../firebase';
import {
  setDoc,
  doc,
  getDoc,
  serverTimestamp,
  collection,
  addDoc,
  updateDoc,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Avatar,
  Grid,
  CircularProgress,
} from '@mui/material';
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';
import SnowflakeIcon from '@mui/icons-material/AcUnit';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const DEFAULT_AVATARS = [
  'https://firebasestorage.googleapis.com/v0/b/otra-151ec.appspot.com/o/avatars%2Favatar-default1.png?alt=media&token=ce2004a8-1b31-441a-b74e-fb4e9eb46391',
  'https://firebasestorage.googleapis.com/v0/b/otra-151ec.appspot.com/o/avatars%2Favatar-default2.png?alt=media&token=1a1307d7-bf1b-49a0-91be-33c9d55e71f8',
  'https://firebasestorage.googleapis.com/v0/b/otra-151ec.appspot.com/o/avatars%2Favatar-default3.png?alt=media&token=fd82dba1-e23d-4e99-9f7d-1ec1a0d4d397',
  'https://firebasestorage.googleapis.com/v0/b/otra-151ec.appspot.com/o/avatars%2Favatar-default4.png?alt=media&token=b17985d5-781e-4327-b967-268dfdc06e7d',
];

function SignUp() {
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPhone, setConfirmPhone] = useState('');
  const [error, setError] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [referrer, setReferrer] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState('');
  const [avatarUrl, setAvatarUrl] = useState(DEFAULT_AVATARS[0]);
  const [avatarFile, setAvatarFile] = useState(null);
  const [selectedDefaultAvatar, setSelectedDefaultAvatar] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Solo permite letras (mayúsculas o minúsculas, mínimo 3)
  const isUsernameValid = /^[a-zA-Z]{3,}$/.test(username);

  // Capturar referidor y fondo random
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const refParam = searchParams.get('ref');
    if (refParam) setReferrer(refParam.trim().toLowerCase());
    const backgroundImages = [
      'https://i.pinimg.com/originals/8a/e0/0e/8ae00e610f195d92a1471c54df0c55ec.jpg',
      'https://watermark.lovepik.com/photo/50072/2231.jpg_wh1200.jpg',
      'https://i.pinimg.com/originals/a1/11/eb/a111eb6da88484397d8b0068ee4e524e.jpg',
      'https://e1.pxfuel.com/desktop-wallpaper/755/363/desktop-wallpaper-6-city-street-backgrounds-city-roads.jpg',
      'https://a.travel-assets.com/findyours-php/viewfinder/images/res40/74000/74993-Santiago.jpg',
    ];
    setBackgroundImage(backgroundImages[Math.floor(Math.random() * backgroundImages.length)]);
  }, [location]);

  // Seleccionar avatar por defecto
  const handleSelectDefaultAvatar = (idx) => {
    setSelectedDefaultAvatar(idx);
    setAvatarUrl(DEFAULT_AVATARS[idx]);
    setAvatarFile(null);
  };

  // Subir avatar personalizado
  const handleAvatarFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setSelectedDefaultAvatar(-1);
      setAvatarUrl(URL.createObjectURL(file));
    }
  };

  // Chequear si el usuario ya existe en Firestore
  const checkUsernameAvailability = async (newUsername) => {
    if (!/^[a-zA-Z]{3,}$/.test(newUsername)) {
      setUsernameError('El apodo debe tener solo letras y al menos 3 caracteres.');
      return;
    }
    setIsCheckingUsername(true);
    const userDoc = await getDoc(doc(db, 'users', newUsername.toLowerCase()));
    setIsCheckingUsername(false);
    if (userDoc.exists()) {
      setUsernameError('El nombre de usuario ya está en uso.');
    } else {
      setUsernameError('');
    }
  };

  // Cada vez que se cambia el username, chequear disponibilidad
  useEffect(() => {
    if (username.length >= 3) {
      const timeout = setTimeout(() => checkUsernameAvailability(username), 500); // Evita spam de consultas
      return () => clearTimeout(timeout);
    } else {
      setUsernameError('');
    }
  }, [username]);

  // Registro
  const handleSignUp = async (e) => {
    e.preventDefault();
    if (usernameError) return;

    try {
      const normalizedUsername = username.trim().toLowerCase();
      const normalizedFullName = fullName.trim();
      const normalizedPhone = phone.trim();

      const phoneRegex = /^[1-9]\d{9,14}$/;
      if (!phoneRegex.test(normalizedPhone)) {
        setError('Por favor, introduce un número de teléfono válido. Debe contener entre 10 y 15 dígitos y no debe empezar con 0.');
        return;
      }
      if (confirmPhone !== phone) {
        setError('Los números de teléfono no coinciden.');
        return;
      }

      // Confirmar usuario no exista justo antes de registrar (prevención doble)
      const userDoc = await getDoc(doc(db, 'users', normalizedUsername));
      if (userDoc.exists()) {
        setUsernameError('El nombre de usuario ya está en uso.');
        return;
      }

      // Verificar si el número de teléfono ya está registrado
      const usersQuery = collection(db, 'users');
      const snapshot = await getDocs(query(usersQuery, where('phone', '==', normalizedPhone)));
      if (!snapshot.empty) {
        setError('El número de teléfono ya está en uso.');
        return;
      }

      let finalAvatarUrl = avatarUrl;
      if (avatarFile) {
        setIsUploading(true);
        const storageRef = ref(storage, `avatars/${normalizedUsername}-${Date.now()}`);
        await uploadBytes(storageRef, avatarFile);
        finalAvatarUrl = await getDownloadURL(storageRef);
        setIsUploading(false);
      }

      // Guardar usuario en Firestore
      const userDocRef = doc(db, 'users', normalizedUsername);
      await setDoc(userDocRef, {
        fullName: normalizedFullName,
        username: normalizedUsername,
        phone: normalizedPhone,
        password,
        createdAt: serverTimestamp(),
        avatarUrl: finalAvatarUrl,
        online: false,
        firstTime: true,
        referrer,
        monOro: 0,
        isBlocked: false,
        numerologo: false,
        numVip: false,
        role: 'normal',
      });

      // Registrar CoinHistory
      const userCoinHistoryRef = collection(db, `users/${normalizedUsername}/CoinHistory`);
      await addDoc(userCoinHistoryRef, {
        type: 'Credit',
        amount: 0,
        timestamp: serverTimestamp(),
        description: `Tu cuenta está lista, gracias por registrarte${referrer ? ' a través de ' + referrer : ''}.`,
      });

      // Si hay referidor, acreditarle monedas
      if (referrer) {
        const referrerDocRef = doc(db, 'users', referrer);
        const referrerDoc = await getDoc(referrerDocRef);
        if (referrerDoc.exists()) {
          const referrerData = referrerDoc.data();
          const sistemaFondosRef = doc(db, 'sistema', 'fondos');
          const sistemaFondosData = (await getDoc(sistemaFondosRef)).data();
          const sistemaFondosActuales = sistemaFondosData.fondos || 0;
          if (sistemaFondosActuales < 0.4) throw new Error('Fondos insuficientes en el sistema para realizar la transacción.');
          await updateDoc(sistemaFondosRef, { fondos: sistemaFondosActuales - 0.4 });
          const updatedMonedasReferidor = (referrerData.monOro || 0) + 0.4;
          await updateDoc(referrerDocRef, { monOro: updatedMonedasReferidor });
          const referrerCoinHistoryRef = collection(db, `users/${referrer}/CoinHistory`);
          await addDoc(referrerCoinHistoryRef, {
            type: 'Deposit',
            amount: 0.4,
            timestamp: serverTimestamp(),
            relatedUser: 'Sistema',
            description: `Ganaste 0.4 monedas porque ${normalizedUsername} se inscribió a través de tu enlace.`,
          });
        }
      }

      setError('');
      localStorage.setItem('username', normalizedUsername);
      navigate('/chat');
    } catch (error) {
      console.error('Error al registrar usuario:', error);
      setError(error.message);
    }
  };

  const handleWhatsAppContact = () => {
    const whatsappUrl = 'https://wa.me/18096245631?text=Hola, necesito asistencia con mi cuenta.';
    window.open(whatsappUrl, '_blank');
  };

  return (
    <Container
      maxWidth="sm"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          p: 4,
          backgroundColor: 'rgba(255, 255, 255, 0.92)',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          position: 'relative',
        }}
      >
        <HolidayVillageIcon
          sx={{
            fontSize: 60,
            color: 'green',
            position: 'absolute',
            top: -30,
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        />
        <Typography component="h1" variant="h5" sx={{ color: '#d32f2f', fontFamily: 'Pacifico, cursive' }}>
          ¡Regístrate y Únete!
        </Typography>
        {error && <Typography color="error">{error}</Typography>}

        <Box component="form" onSubmit={handleSignUp} sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600 }}>
            Elige tu Avatar
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2, mt: 1 }}>
            {DEFAULT_AVATARS.map((url, idx) => (
              <Grid item key={url}>
                <Avatar
                  src={url}
                  sx={{
                    width: 56,
                    height: 56,
                    border: idx === selectedDefaultAvatar ? '3px solid #1976d2' : '2px solid #e0e0e0',
                    cursor: 'pointer',
                    transition: 'border 0.2s',
                  }}
                  onClick={() => handleSelectDefaultAvatar(idx)}
                />
              </Grid>
            ))}
            <Grid item>
              <label htmlFor="upload-avatar">
                <input
                  type="file"
                  accept="image/*"
                  id="upload-avatar"
                  style={{ display: 'none' }}
                  onChange={handleAvatarFileChange}
                />
                <Avatar
                  src={avatarFile ? avatarUrl : ''}
                  sx={{
                    width: 56,
                    height: 56,
                    border: avatarFile ? '3px solid #388e3c' : '2px dashed #aaa',
                    cursor: 'pointer',
                    background: !avatarFile ? '#f1f1f1' : 'transparent',
                  }}
                >
                  {avatarFile ? '' : '+'}
                </Avatar>
              </label>
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {avatarFile
              ? 'Avatar personalizado seleccionado'
              : selectedDefaultAvatar >= 0
              ? 'Avatar predeterminado seleccionado'
              : 'No has seleccionado avatar'}
          </Typography>

          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="fullName"
            label="Nombre Completo"
            name="fullName"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            autoFocus
            sx={{ bgcolor: 'white', borderRadius: 1 }}
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="username"
            label="Apodo"
            name="username"
            value={username}
            onChange={(e) => {
              const value = e.target.value.replace(/[^a-zA-Z]/g, '');
              setUsername(value);
            }}
            error={username.length > 0 && (!isUsernameValid || !!usernameError)}
            helperText={
              username.length > 0 && usernameError
                ? usernameError
                : 'Solo letras, sin espacios ni números (mínimo 3 caracteres)'
            }
            inputProps={{ maxLength: 16 }}
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="phone"
            label="Número de teléfono"
            name="phone"
            value={phone}
            onChange={(e) => {
              const sanitizedValue = e.target.value.replace(/[-\s]/g, '');
              setPhone(sanitizedValue);
            }}
            sx={{ bgcolor: 'white', borderRadius: 1 }}
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="confirmPhone"
            label="Confirmar Número de teléfono"
            name="confirmPhone"
            value={confirmPhone}
            onChange={(e) => {
              const sanitizedValue = e.target.value.replace(/[-\s]/g, '');
              setConfirmPhone(sanitizedValue);
            }}
            error={confirmPhone.length > 0 && confirmPhone !== phone}
            helperText={
              confirmPhone.length > 0 && confirmPhone !== phone
                ? 'Los números no coinciden.'
                : ''
            }
            sx={{ bgcolor: 'white', borderRadius: 1 }}
          />
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="password"
            label="Clave (mínimo 6 dígitos)"
            name="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            inputProps={{ minLength: 6 }}
            error={password.length > 0 && password.length < 6}
            helperText={
              password.length > 0 && password.length < 6
                ? 'La clave debe tener al menos 6 dígitos'
                : ''
            }
            sx={{ bgcolor: 'white', borderRadius: 1 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 2,
              mb: 2,
              bgcolor: isUsernameValid && !usernameError ? '#d32f2f' : '#e0e0e0',
              color: isUsernameValid && !usernameError ? 'white' : 'black',
              '&:hover': isUsernameValid && !usernameError ? { bgcolor: '#c62828' } : null,
              fontWeight: 'bold',
            }}
            disabled={!isUsernameValid || !!usernameError || isUploading}
            startIcon={isUploading && <CircularProgress size={20} color="inherit" />}
          >
            {isUploading ? 'Subiendo avatar...' : 'Inscribirse'}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            sx={{ mt: 0 }}
            onClick={() => navigate('/')}
          >
            Volver al Inicio
          </Button>
          <Button
            fullWidth
            variant="contained"
            startIcon={<WhatsAppIcon />}
            onClick={handleWhatsAppContact}
            sx={{
              mt: 1,
              bgcolor: '#25d366',
              color: 'white',
              '&:hover': { bgcolor: '#1ebe57' },
            }}
          >
            Contactar por WhatsApp
          </Button>
        </Box>
        <SnowflakeIcon sx={{ fontSize: 30, color: '#d32f2f', mt: 2 }} />
      </Box>
    </Container>
  );
}

export default SignUp;
